/**
 * usePaymentNotifications Hook
 *
 * Tells the customer their payment was decided, wherever they happen to be in the console.
 *
 * Verification is not something the customer waits in front of. A bank transfer is approved
 * by an operator minutes or hours after the receipt was uploaded, and the deposit screen ends
 * at a "submitted" panel that the customer has long since navigated away from — so every
 * announcement that lived on a payment screen simply never fired for that route. A QR bill was
 * covered only because it happens to leave the customer parked on `/billing/payment/:id`.
 *
 * This closes that: one listener, mounted in the authenticated layout, that survives navigation.
 * It also files the event in the notification bell, so someone who was on another tab still finds
 * out, and refreshes the balance figure the header and dashboard read.
 *
 * It stays silent for a payment a screen is already watching — see `lib/paymentWatchers` — so
 * the wizards and the payment-status page keep announcing settlement in their own words at the
 * right point in their own flow, and nobody gets told twice.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "i18next";

import { socketIOManager, SocketIOEvent } from "@/lib/socketio";
import { paymentService } from "@/services/payment.service";
import { billingService } from "@/services/billing.service";
import {
  hasPaymentWatcher,
  paymentSettledToastId,
} from "@/lib/paymentWatchers";
import { formatCurrency, toCurrencyCode } from "@/lib/currency";
import { getPayableBalance } from "@/lib/payableBalance";
import { isTopUpPayment } from "@/lib/paymentPurpose";
import { useNotificationStore } from "@/stores/notificationStore";
import type { PaymentOrder } from "@/types/payment";

interface PaymentVerifiedPayload {
  paymentId?: string;
  tenantId?: string;
  amount?: number;
  verifiedBy?: string;
}

interface PaymentRejectedPayload {
  paymentId?: string;
  tenantId?: string;
  rejectedBy?: string;
  reason?: string;
}

interface PaymentRefundedPayload {
  paymentId?: string;
  tenantId?: string;
  amount?: number;
  refundedBy?: string;
  reason?: string;
}

interface PaymentCancelledPayload {
  paymentId?: string;
  tenantId?: string;
  reason?: string;
}

interface OrderClosedPayload {
  orderId?: string;
  tenantId?: string;
  orderNumber?: string;
  status?: string;
}

/** Every cache that shows a balance figure. Refetched, never patched from the event. */
const BALANCE_KEYS = [["accountBalance"], ["billing", "balance"]] as const;

export function usePaymentNotifications(): void {
  const queryClient = useQueryClient();

  // The action alone, not useNotifications(): that hook also selects the notifications array,
  // and this one is mounted in AppLayout — subscribing to the array there would re-render the
  // whole authenticated tree on every notification, for a value nothing here reads. Store
  // actions have a stable identity, so the effect below never re-subscribes.
  const addNotification = useNotificationStore(
    (state) => state.addNotification,
  );

  useEffect(() => {
    const refreshBalance = () => {
      for (const key of BALANCE_KEYS) {
        queryClient.invalidateQueries({ queryKey: [...key] });
      }
    };

    /**
     * Every list and detail view that prints a payment's STATUS, refreshed together.
     *
     * The toast used to be the only thing that moved: an operator approved or declined a payment,
     * the customer was told so within a couple of seconds, and the row they were looking at on
     * "Lịch sử thanh toán" went on saying "Chờ xác minh" until they reloaded the page by hand.
     * Two contradictory answers on one screen, and the stale one is the one that looks official.
     *
     * `["paymentHistory"]` is deliberately the bare prefix: that list keys itself by its filter
     * and paging state (`["paymentHistory", queryParams]`), so naming the prefix invalidates
     * whichever page and filter the customer currently has open, instead of one guessed variant.
     */
    const refreshPaymentViews = (paymentId?: string) => {
      queryClient.invalidateQueries({ queryKey: ["paymentHistory"] });
      if (paymentId) {
        queryClient.invalidateQueries({
          queryKey: ["paymentOrder", paymentId],
        });
      }
    };

    /**
     * The machines a refused order had already reserved, refreshed together with the payment.
     *
     * Rejecting an order payment now cancels the order AND retires the rows it had reserved:
     * the console writes a Resource row per line at checkout, before any money moves, so a VM
     * order leaves three rows sitting in `Pending` from the second it is created. They are
     * retired to `Terminated`, which is what drops them out of every tenant-facing query — but
     * the customer's open list is a cached answer from before that happened. Without this it
     * keeps showing a machine that no longer exists anywhere, for as long as the tab stays open.
     *
     * The bare `["resources"]` prefix on purpose, same reasoning as `["paymentHistory"]` above:
     * the list keys itself by filter and paging, and the detail view by id, so naming the prefix
     * catches whichever of them the customer actually has open. The one exclusion is the VM
     * credentials: every read of them writes a credentials_viewed audit row the customer sees, and
     * a payment changes no password, so an open Credentials tab must not re-read on its own.
     */
    const refreshResourceViews = () => {
      queryClient.invalidateQueries({
        queryKey: ["resources"],
        predicate: (query) => query.queryKey[1] !== "credentials",
      });
    };

    /**
     * What an APPROVED order payment moves, beyond the payment's own status.
     *
     * A verified payment settles its order and fulfils it, and for a renewal that means the
     * machine's paid-through date moves and a suspended machine is brought back up. None of that
     * reached the screen the customer was actually on: they got the toast, went to look at the VM
     * they had just renewed, and the console still showed the old expiry — and the toast is the
     * thing that looks wrong when the page disagrees with it.
     */
    const refreshOrderViews = () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    };

    const file = (
      type: "success" | "error" | "info",
      title: string,
      message: string,
      paymentId?: string,
      toastId?: string,
    ) => {
      addNotification({
        id: crypto.randomUUID(),
        // The bell groups by kind, and a settled payment is a billing event before it is a
        // generic success — that is what makes the money entries findable in the panel.
        type: "billing",
        title,
        message,
        timestamp: new Date().toISOString(),
        read: false,
        actionUrl: paymentId ? `/billing/payment/${paymentId}` : "/billing",
      });

      // A refund is "info" on purpose: green would celebrate money leaving the customer's
      // balance, and red would read as something having gone wrong when an operator did this
      // deliberately.
      const show =
        type === "success"
          ? toast.success
          : type === "error"
            ? toast.error
            : toast.info;

      // An id only when the caller names one: sonner then updates a toast already on screen
      // under that id rather than adding a second.
      if (toastId) {
        show(title, { id: toastId, description: message });
        return;
      }

      show(title, { description: message });
    };

    /**
     * The payment behind an event, from cache or from the server.
     *
     * Both announcements turn on the same fact — top-up or order payment — and neither event
     * carries an orderId. The customer is by definition NOT on a payment screen when these
     * fire, so the cache is usually empty: fetch rather than guess, since one small GET is what
     * buys the right sentence. Undefined when even that fails, and each caller then falls back
     * to wording that is true whichever kind it was.
     */
    const loadPayment = async (paymentId?: string) => {
      if (!paymentId) return undefined;

      return (
        queryClient.getQueryData<PaymentOrder>(["paymentOrder", paymentId]) ??
        (await queryClient
          .fetchQuery({
            queryKey: ["paymentOrder", paymentId],
            queryFn: () => paymentService.getPaymentOrder(paymentId),
          })
          .catch(() => undefined))
      );
    };

    /** Appends the operator's reason, which is the part that explains an unexpected change. */
    const withReason = (sentence: string, reason?: string) =>
      reason?.trim()
        ? `${sentence} ${i18n.t("billing:toast.reasonPrefix")} ${reason.trim()}`
        : sentence;

    /**
     * The wallet as it stands right now, or undefined when it cannot be read.
     *
     * Fetched rather than taken from the `balance:updated` event that accompanies a verified
     * deposit: the two events are separate publishes and nothing orders them, so a listener
     * that waited for the other one would be announcing on a race. `refreshBalance` has already
     * invalidated this key by the time this runs, so `fetchQuery` returns the post-credit figure
     * rather than the cached pre-credit one.
     */
    const loadBalance = async () =>
      queryClient
        .fetchQuery({
          queryKey: ["accountBalance"],
          queryFn: () => billingService.getBalance(),
        })
        .catch(() => undefined);

    /**
     * A deposit credits the wallet; an order payment settles the order and credits nothing,
     * so one sentence cannot serve both — "your order is being processed" is simply wrong
     * for a top-up, and that is the exact case this listener exists for.
     *
     * For a deposit the sentence names both figures — what went in, and what the wallet holds
     * now — because "your balance has been credited" is precisely the answer that sent the
     * customer to go and look. Both are needed for that to be worth reading, so a missing
     * balance falls back to the old wording rather than printing half a sentence.
     */
    const announceVerified = async (paymentId?: string, amount?: number) => {
      const payment = await loadPayment(paymentId);
      const isDeposit = payment !== undefined && isTopUpPayment(payment);

      let description = i18n.t(
        isDeposit
          ? "billing:toast.verifiedDepositDesc"
          : "billing:toast.verifiedOrderDesc",
      );

      if (isDeposit && typeof amount === "number") {
        const balance = await loadBalance();
        const payable = getPayableBalance(balance);

        if (balance && payable !== null) {
          description = i18n.t("billing:toast.verifiedDepositDescWithBalance", {
            amount: formatCurrency(amount, toCurrencyCode(payment.currency)),
            balance: formatCurrency(payable, toCurrencyCode(balance.currency)),
          });
        }
      }

      file(
        "success",
        i18n.t(
          isDeposit
            ? "billing:toast.verifiedDeposit"
            : "billing:toast.verifiedOrder",
        ),
        description,
        paymentId,
        // The status page may have announced this same settlement from its poll and left
        // before this event arrived; the shared id keeps that to one toast.
        paymentId ? paymentSettledToastId(paymentId) : undefined,
      );
    };

    const unsubVerified = socketIOManager.subscribe<PaymentVerifiedPayload>(
      SocketIOEvent.PaymentVerified,
      (data) => {
        const paymentId = data?.paymentId;

        // The balance moves whether or not anyone is looking, so refresh regardless of who
        // ends up doing the talking. Same for the status views: a screen that watches this
        // payment does its own announcing, but it is not the only screen showing the status.
        refreshBalance();
        refreshPaymentViews(paymentId);
        refreshOrderViews();
        refreshResourceViews();

        if (paymentId && hasPaymentWatcher(paymentId, "verified")) return;

        void announceVerified(paymentId, data?.amount);
      },
    );

    const unsubRejected = socketIOManager.subscribe<PaymentRejectedPayload>(
      SocketIOEvent.PaymentRejected,
      (data) => {
        const paymentId = data?.paymentId;
        // Rejection cancels the order too (R9), so the customer's order views must move with it,
        // same as verify does.
        refreshPaymentViews(paymentId);
        refreshOrderViews();
        refreshResourceViews();

        if (paymentId && hasPaymentWatcher(paymentId, "rejected")) return;

        file(
          "error",
          i18n.t("billing:toast.rejected"),
          // The operator's reason is the only thing that tells the customer what to do
          // next, so it is preferred over the generic sentence when one was given.
          data?.reason || i18n.t("billing:toast.rejectedFallback"),
          paymentId,
        );
      },
    );

    /**
     * A refund CREDITS the balance. Nothing is ever paid back to a bank — no gateway refund exists
     * — so what an operator's refund actually does is hand the amount back as spendable balance,
     * for the customer to order another machine with. The copy says exactly that and nothing more.
     *
     * Only an order payment can be refunded (a deposit is refused with Reject before it reaches
     * the wallet), so there is no second case to word differently any more. The amount is spelled
     * out rather than left for the customer to infer from a figure that moved.
     */
    const announceRefunded = async (data?: PaymentRefundedPayload) => {
      const paymentId = data?.paymentId;
      const payment = await loadPayment(paymentId);

      const amount =
        typeof data?.amount === "number"
          ? formatCurrency(data.amount, (payment?.currency ?? "VND") as never)
          : "";

      // Without an amount, the sentence still has to be true: say the money is back, just not how
      // much. Inventing a figure here would be worse than omitting one.
      const body = amount
        ? i18n.t("billing:toast.refundedDesc", { amount })
        : i18n.t("billing:toast.refundedFallback");

      file(
        "info",
        i18n.t("billing:toast.refunded"),
        withReason(body, data?.reason),
        paymentId,
      );
    };

    const unsubRefunded = socketIOManager.subscribe<PaymentRefundedPayload>(
      SocketIOEvent.PaymentRefunded,
      (data) => {
        const paymentId = data?.paymentId;

        // The balance moved, so refresh it whoever ends up doing the talking.
        refreshBalance();
        refreshPaymentViews(paymentId);

        if (paymentId) {
          queryClient.invalidateQueries({
            queryKey: ["billing", "transactions"],
          });
        }

        if (paymentId && hasPaymentWatcher(paymentId, "refunded")) return;

        void announceRefunded(data);
      },
    );

    // Admin bulk cancel (N5): no money moved, but the payment needs to stop showing "awaiting
    // verification". Only a wallet top-up's order is closed with it; a purchase or renewal order stays
    // open to be paid another way, and the order views are refreshed for both.
    const unsubCancelled = socketIOManager.subscribe<PaymentCancelledPayload>(
      SocketIOEvent.PaymentCancelled,
      (data) => {
        const paymentId = data?.paymentId;

        // No balance or ledger refresh: a cancel moves no money, and a bulk cancel fires this
        // once per payment — the wallet would be refetched N times for nothing.
        refreshPaymentViews(paymentId);
        refreshOrderViews();

        // The operator's reason is shown, same as a rejection: the payment screen prints it as the
        // cancellation reason, so the toast and the page it links to say the same thing.
        file(
          "info",
          i18n.t("billing:toast.cancelled"),
          withReason(i18n.t("billing:toast.cancelledFallback"), data?.reason),
          paymentId,
        );
      },
    );

    // Admin Cancel order — the only publisher of this event. The order's Pending machines are
    // retired with it, so this refreshes resources too.
    const unsubOrderClosed = socketIOManager.subscribe<OrderClosedPayload>(
      SocketIOEvent.OrderClosed,
      (data) => {
        refreshOrderViews();
        refreshResourceViews();

        // Cancel order also cancels the payment open on the order, and publishes nothing for it. The
        // event names no payment, so every cached payment is refetched: otherwise a customer sitting
        // on that payment's transfer instructions keeps reading "awaiting verification".
        queryClient.invalidateQueries({ queryKey: ["paymentHistory"] });
        queryClient.invalidateQueries({ queryKey: ["paymentOrder"] });

        file(
          "info",
          i18n.t("billing:toast.orderClosed"),
          i18n.t("billing:toast.orderClosedDesc", {
            orderNumber: data?.orderNumber ?? "",
          }),
        );
      },
    );

    return () => {
      unsubVerified();
      unsubRejected();
      unsubRefunded();
      unsubCancelled();
      unsubOrderClosed();
    };
  }, [queryClient, addNotification]);
}
